import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { orderApi } from '../../lib/supabase-api';
import { Order } from '../../types';

const paymentMethodLabel = (method?: string): string => {
  switch (method) {
    case 'crypto': return 'Cryptocurrency';
    case 'bank': return 'Bank Transfer';
    case 'virtual': return 'Virtual Account';
    case 'card': return 'Credit Card';
    case 'phone': return 'Mobile Payment';
    case 'manual': return 'Manual / Contact Support';
    default: return method || '-';
  }
};

const asAddress = (a: any): {
  name?: string;
  phone?: string;
  zipCode?: string;
  address?: string;
  detailAddress?: string;
  deliveryRequest?: string;
} => (typeof a === 'object' && a !== null ? a : {});

const OrderComplete: React.FC = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isLoggedIn) { navigate('/login'); return; }
    if (!orderId) { navigate('/'); return; }

    let cancelled = false;
    (async () => {
      const fetched = await orderApi.getById(orderId);
      if (cancelled) return;
      if (!fetched) { navigate('/'); return; }
      setOrder(fetched);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [orderId, isLoggedIn, navigate]);

  if (loading || !order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold-400 mx-auto"></div>
          <p className="mt-4 text-warm-600">Loading order information...</p>
        </div>
      </div>
    );
  }

  const shipping = asAddress(order.shippingAddress);
  const shippingFee = order.shippingFee ?? 0;
  const discount = order.discountAmount ?? 0;
  const subtotal = order.subtotal ?? (order.totalAmount - shippingFee + discount);

  return (
    <div className="min-h-screen bg-cream-100">
      <div className="section-container py-8 sm:py-12">
        {/* Success Message */}
        <div className="bg-cream-50 rounded-xl border border-warm-200 shadow-card p-8 text-center mb-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="font-display text-display-sm text-forest-900 mb-2">
            {order.paymentStatus === 'paid' ? 'Payment confirmed!' : 'Your order has been placed!'}
          </h1>
          <p className="text-warm-600 mb-4">
            Order Number: <span className="font-semibold text-forest-900">{order.orderNumber}</span>
          </p>
          <div className="text-sm text-warm-500 space-y-2">
            {order.paymentStatus === 'paid' ? (
              <>
                <p>Your payment has been received and your order is now in the queue.</p>
                <p className="font-medium text-forest-700">
                  We'll email you again as soon as it ships. Track progress from My Orders.
                </p>
              </>
            ) : (
              <>
                <p>An order confirmation and payment instructions have been sent to your email.</p>
                <p className="font-medium text-gold-500">
                  Please check your email and complete the payment following the instructions.
                </p>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Order Information */}
          <div className="bg-cream-50 rounded-xl border border-warm-200 shadow-card p-6">
            <h2 className="text-lg font-semibold text-forest-900 mb-4">Order Information</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-warm-600">Order Number</span>
                <span className="font-medium text-forest-900">{order.orderNumber}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-warm-600">Order Date</span>
                <span className="font-medium text-forest-900">
                  {new Date(order.createdAt).toLocaleString('en-US')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-warm-600">Payment Method</span>
                <span className="font-medium text-forest-900">
                  {paymentMethodLabel(order.paymentMethod)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-warm-600">Order Status</span>
                <span className="font-medium text-green-600">
                  {order.paymentStatus === 'paid' ? 'Payment Complete' : 'Awaiting Payment'}
                </span>
              </div>
            </div>
          </div>

          {/* Shipping Information */}
          <div className="bg-cream-50 rounded-xl border border-warm-200 shadow-card p-6">
            <h2 className="text-lg font-semibold text-forest-900 mb-4">Shipping Information</h2>
            <div className="space-y-3">
              <div>
                <span className="text-warm-600 block">Recipient</span>
                <span className="font-medium text-forest-900">{shipping.name || order.userName}</span>
              </div>
              <div>
                <span className="text-warm-600 block">Phone</span>
                <span className="font-medium text-forest-900">{shipping.phone || order.userPhone || '-'}</span>
              </div>
              <div>
                <span className="text-warm-600 block">Shipping Address</span>
                <div className="font-medium text-forest-900">
                  <div>
                    {shipping.zipCode && `(${shipping.zipCode}) `}
                    {shipping.address || (typeof order.shippingAddress === 'string' ? order.shippingAddress : '-')}
                  </div>
                  {shipping.detailAddress && <div>{shipping.detailAddress}</div>}
                </div>
              </div>
              {shipping.deliveryRequest && (
                <div>
                  <span className="text-warm-600 block">Delivery Instructions</span>
                  <span className="font-medium text-forest-900">{shipping.deliveryRequest}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Ordered Items */}
        <div className="bg-cream-50 rounded-xl border border-warm-200 shadow-card p-6 mt-8">
          <h2 className="text-lg font-semibold text-forest-900 mb-4">Ordered Items</h2>
          <div className="space-y-4">
            {order.items.map((item, idx) => (
              <div key={`${item.productId}-${idx}`} className="flex items-center space-x-4 p-4 border border-warm-200 rounded-xl">
                <img
                  src={item.image || '/images/placeholder.png'}
                  alt={item.productName}
                  className="w-20 h-20 object-contain rounded-md bg-cream-100"
                />
                <div className="flex-1">
                  <h3 className="font-medium text-forest-900">{item.productName}</h3>
                  {item.manufacturer && (
                    <p className="text-sm text-warm-600">{item.manufacturer}</p>
                  )}

                  <div className="mt-2 p-2 bg-forest-50 border border-forest-200 rounded">
                    <div className="text-xs text-forest-800 font-medium">
                      {item.pillQuantity
                        ? `${item.pillQuantity} pills x ${item.quantity} boxes = ${item.pillQuantity * item.quantity} pills`
                        : `Quantity: ${item.quantity}`}
                    </div>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="text-sm font-semibold text-forest-900">
                        ${item.price.toLocaleString()}
                      </span>
                      <span className="text-xs text-warm-500">per unit</span>
                    </div>
                  </div>

                  <div className="mt-2 text-right">
                    <span className="text-sm font-medium text-forest-900">
                      Line total: ${(item.price * item.quantity).toLocaleString()}
                    </span>
                  </div>
                </div>
                <Link
                  to={`/product/${item.productId}`}
                  className="text-gold-400 hover:text-forest-800 text-sm font-medium"
                >
                  View Product
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* Payment Summary */}
        <div className="bg-cream-50 rounded-xl border border-warm-200 shadow-card p-6 mt-8">
          <h2 className="text-lg font-semibold text-forest-900 mb-4">Payment Summary</h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-warm-600">Subtotal</span>
              <span className="text-forest-900">${subtotal.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-warm-600">Shipping</span>
              <span className="text-forest-900">
                {shippingFee === 0 ? 'Free' : `$${shippingFee.toLocaleString()}`}
              </span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between">
                <span className="text-warm-600">Discount</span>
                <span className="text-red-600">-${discount.toLocaleString()}</span>
              </div>
            )}
            <div className="border-t pt-3">
              <div className="flex justify-between">
                <span className="text-lg font-semibold text-forest-900">Order Total</span>
                <span className="text-lg font-semibold text-gold-400">${order.totalAmount.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Shipping Notice */}
        <div className="bg-forest-50 border border-forest-200 rounded-xl p-6 mt-8">
          <h3 className="text-lg font-semibold text-forest-900 mb-3">Shipping Notice</h3>
          <div className="space-y-2 text-sm text-forest-800">
            <p>- International shipping typically takes a minimum of 2 weeks for delivery.</p>
            <p>- You will be notified when your order is ready for shipment.</p>
            <p>- You can track your delivery from My Account &gt; Order History.</p>
            <p>- For shipping inquiries, please contact our customer service.</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
          <Link to="/mypage" className="btn-primary text-center">
            View My Orders
          </Link>
          <Link to="/" className="btn-secondary text-center">
            Continue Shopping
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OrderComplete;
